// src/pages/DoctorTriageReportsPage.jsx
// Fix 5 — Triage Reports page for doctor portal.
// Route: /doctor/triage-reports?patientId=<uuid>
// Backend: GET /api/doctor/triage-reports?patientId=  (already live)

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppShell } from '../components/Layout';
import { triageReportAPI, doctorPatientAPI } from '../services/doctorApi';
import Icon from '../components/Icon';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

// ── Compact Previu Age summary (patient-level, not per-triage) ──────────────
function PreviuAgeSummary({ patientId }) {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!patientId) return;
    const token = localStorage.getItem('previu_token') || localStorage.getItem('token');
    fetch(`${API_BASE}/previu-age/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.status === 204 ? null : r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [patientId]);

  if (loading || !data) return null;

  const previuAge = data.previuAgeYears != null ? parseFloat(data.previuAgeYears).toFixed(1) : null;
  const delta     = data.ageDeltaYears  != null ? parseFloat(data.ageDeltaYears).toFixed(1)  : null;
  const chronAge  = data.chronologicalAgeYears;
  const direction = data.yoyDeltaDirection;

  const dColor = delta == null ? 'var(--text-muted)'
    : parseFloat(delta) <= -2 ? 'var(--green)'
    : parseFloat(delta) <= 1  ? 'var(--amber-text)'
    : 'var(--red)';

  const yoyIcon = direction === 'IMPROVING' ? '↓' : direction === 'DETERIORATING' ? '↑' : '→';

  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: 20,
      display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap',
    }}>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '.08em', color: 'var(--text-muted)', marginBottom: 2 }}>
          Previu Age
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 32, fontWeight: 500, color: 'var(--text-primary)', lineHeight: 1 }}>
            {previuAge ?? '—'}
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>yrs biological</span>
        </div>
        {chronAge != null && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            Calendar age: {chronAge} yrs
          </div>
        )}
      </div>
      {delta != null && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '.08em', color: 'var(--text-muted)', marginBottom: 2 }}>
            Delta
          </div>
          <div style={{ fontSize: 22, fontWeight: 500, color: dColor, lineHeight: 1 }}>
            {parseFloat(delta) >= 0 ? `+${delta}` : delta} yrs
          </div>
          <div style={{ fontSize: 11, color: dColor, marginTop: 2 }}>
            {parseFloat(delta) <= -2 ? 'Biologically younger'
              : parseFloat(delta) <= 1 ? 'On track' : 'Biologically older'}
          </div>
        </div>
      )}
      {direction && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '.08em', color: 'var(--text-muted)', marginBottom: 2 }}>
            Year-on-year
          </div>
          <div style={{ fontSize: 16, fontWeight: 500,
            color: direction === 'IMPROVING' ? 'var(--green)'
              : direction === 'DETERIORATING' ? 'var(--red)' : 'var(--text-muted)' }}>
            {yoyIcon} {direction.charAt(0) + direction.slice(1).toLowerCase()}
          </div>
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto',
        fontStyle: 'italic', alignSelf: 'flex-end' }}>
        Based on {data.drawsUsed} draw{data.drawsUsed !== 1 ? 's' : ''} · Ref: Constitution §VI
      </div>
    </div>
  );
}

const RISK_BADGE = {
  HIGH:     'badge-critical',
  MODERATE: 'badge-moderate',
  LOW:      'badge-low',
  UNKNOWN:  'badge-confirmed',
};

const RISK_LABEL = {
  HIGH: 'High', MODERATE: 'Moderate', LOW: 'Low', UNKNOWN: 'Unknown',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

// Skeleton loader — shared .skeleton classes from index.css
function SkeletonList() {
  return (
    <div className="skeleton-list">
      {[1, 2, 3].map(i => (
        <div key={i} className="skeleton skeleton--row" style={{ height: 88 }} />
      ))}
    </div>
  );
}

// Single triage report card — expandable
function TriageReportCard({ report, onViewDetail, patientId }) {
  const [expanded, setExpanded] = useState(false);
  const riskBadge = RISK_BADGE[report.consensusRiskLevel] || 'badge-confirmed';
  const riskLabel = RISK_LABEL[report.consensusRiskLevel] || report.consensusRiskLevel;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 8 }}>
      {/* Header row — always visible */}
      <button
        className="card-row" style={{ border: 'none', borderRadius: 0 }}
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 500, fontSize: 13, color: 'var(--text-primary)' }}>
              {report.triageType === 'BLOOD_TEST' ? 'Blood Test Triage'
                : report.triageType === 'RADIOLOGY' ? 'Radiology'
                : report.triageType || 'Triage'}
            </span>
            <span className={`badge ${riskBadge}`}>{riskLabel}</span>
            {report.hasAcuteAlert && (
              <span className="badge badge-critical" style={{ gap: 4 }}>
                ⚠ Acute alert
              </span>
            )}
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {fmtDateTime(report.createdAt)}
          </span>
        </div>

        {/* Score chips */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {report.consensusRiskScore != null && (
            <span style={{
              fontSize: 18, fontWeight: 700, color: 'var(--text-primary)',
              fontFamily: 'var(--font-display)',
            }}>
              {report.consensusRiskScore}
            </span>
          )}
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {expanded ? '▲' : '▼'}
          </span>
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg-subtle)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Score strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10 }}>
            <div className="stat-card" style={{ textAlign: 'center', padding: '12px' }}>
              <div className="stat-value" style={{
                fontSize: 26,
                color: {
                  HIGH: 'var(--red)', MODERATE: 'var(--amber-text)',
                  LOW: 'var(--green)', UNKNOWN: 'var(--text-muted)',
                }[report.consensusRiskLevel] || 'var(--text-muted)',
              }}>
                {report.consensusRiskScore ?? '—'}
              </div>
              <div className="stat-label" style={{ marginTop: 4, marginBottom: 0 }}>Consensus</div>
            </div>
            {/* Random Forest tile removed (Sep 2026, RF disabled). */}
          </div>

          {/* Acute alert */}
          {report.hasAcuteAlert && report.acuteAlertDetail && (
            <div className="alert alert-error">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <strong>Acute alert:</strong>&nbsp;{report.acuteAlertDetail}
            </div>
          )}

          {/* Key findings preview */}
          {report.claudeKeyFindings && (
            <div className="field-group">
              <div className="field-label">Key findings</div>
              <p className="field-text">
                {report.claudeKeyFindings.slice(0, 300)}
                {report.claudeKeyFindings.length > 300 ? '…' : ''}
              </p>
            </div>
          )}

          {/* Action plan summary */}
          {report.actionPlan?.length > 0 && (
            <div className="field-group">
              <div className="field-label">
                Action plan · {report.actionPlan.length} item{report.actionPlan.length !== 1 ? 's' : ''}
              </div>
              {report.actionPlan.slice(0, 3).map((item, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 8, alignItems: 'flex-start',
                  fontSize: 12, color: 'var(--text-muted)', marginBottom: 4,
                }}>
                  <span className={`badge ${
                    item.priority === 'P1' ? 'badge-critical' :
                    item.priority === 'P2' ? 'badge-moderate' : 'badge-confirmed'
                  }`} style={{ flexShrink: 0, marginTop: 1 }}>
                    {item.priority}
                  </span>
                  <span>{item.title}</span>
                </div>
              ))}
              {report.actionPlan.length > 3 && (
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
                  +{report.actionPlan.length - 3} more items
                </p>
              )}
            </div>
          )}

          {/* CTA — view full detail */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => onViewDetail(patientId)}
            >
              View full report →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Empty state — no patientId provided ──────────────────────────────────────
function EmptyState() {
  const navigate = useNavigate();
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 12, padding: '60px 16px', textAlign: 'center',
    }}>
      <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="trend" size={30} /></div>
      <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }}>
        No patient selected
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 280, margin: 0, lineHeight: 1.5 }}>
        Open a patient's detail page and their triage reports will appear here.
      </p>
      <button
        className="btn btn-primary btn-sm"
        onClick={() => navigate('/doctor/patients')}
      >
        Go to My Patients
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DoctorTriageReportsPage() {
  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();
  const { user }        = useAuth();
  const patientId       = searchParams.get('patientId');

  const [reports,       setReports]      = useState([]);
  const [patientName,   setPatientName]  = useState('');
  const [loading,       setLoading]      = useState(false);
  const [error,         setError]        = useState(null);

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        // Load reports + patient name in parallel
        const [triRes, patRes] = await Promise.allSettled([
          triageReportAPI.listByPatient(patientId),
          doctorPatientAPI.getDetail(patientId, user?.email),
        ]);

        if (cancelled) return;

        if (triRes.status === 'fulfilled') {
          const data = triRes.value.data;
          setReports(Array.isArray(data) ? data : data?.content || []);
        } else {
          setError('Could not load triage reports.');
        }

        if (patRes.status === 'fulfilled') {
          const p = patRes.value.data;
          setPatientName(p ? `${p.firstName} ${p.lastName}` : '');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [patientId, user?.email]);

  const handleViewDetail = (pid) => {
    navigate(`/doctor/patients/${pid}?tab=disease`);
  };

  const breadcrumbs = [
    { label: 'My Patients', to: '/doctor/patients' },
    ...(patientName ? [{ label: patientName, to: `/doctor/patients/${patientId}` }] : []),
    { label: 'Triage Reports' },
  ];

  return (
    <AppShell breadcrumbs={breadcrumbs}>
      <>

        {/* Page header */}
        <div className="page-header fade-in-up">
          <div>
            <div className="page-title">Triage Reports</div>
            {patientName && (
              <div className="page-subtitle">{patientName}</div>
            )}
          </div>
          {patientId && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate(`/doctor/patients/${patientId}`)}
            >
              ← Patient detail
            </button>
          )}
        </div>

        {/* States */}
        {!patientId && <EmptyState />}

        {patientId && loading && <SkeletonList />}

        {patientId && !loading && error && (
          <div className="alert alert-error">{error}</div>
        )}

        {patientId && !loading && !error && reports.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="trend" size={30} /></div>
            <div>No triage reports found for this patient.</div>
            <p style={{ fontSize: 12, marginTop: 6 }}>
              Run a triage from the navigator portal to generate one.
            </p>
          </div>
        )}

        {/* Previu Age summary — patient-level, above report list */}
        {patientId && !loading && <PreviuAgeSummary patientId={patientId} />}

        {/* Report list */}
        {!loading && reports.length > 0 && (
          <>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {reports.length} report{reports.length !== 1 ? 's' : ''} · most recent first
            </div>
            <div>
              {reports.map(report => (
                <TriageReportCard
                  key={report.id}
                  report={report}
                  patientId={patientId}
                  onViewDetail={handleViewDetail}
                />
              ))}
            </div>
          </>
        )}
      </>
    </AppShell>
  );
}

